// ============================================================
// The build bar's small controls, shared by the bar and the editor
// ============================================================

import clsx from '../../utils/clsx';
import styles from '../../styles/Battlefield.module.css';

/** One choice in a radiogroup of chips: a brush tip, a material, a shape. */
export function RadioChip({ checked, testId, onClick, title, children }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      data-testid={testId}
      className={clsx(styles.brush, checked && styles.brushActive)}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

/** A modifier that stays on until pressed again: Hollow, Float. */
export function ModifierToggle({ on, testId, onClick, title, children }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      data-testid={testId}
      className={clsx(styles.brush, styles.modifier, on && styles.brushActive, on && styles.modifierActive)}
      onClick={onClick}
      title={title}
    >
      {children}
    </button>
  );
}

/**
 * MAKING ONE IS NOT AN EDIT. It lived inside the editor, so a new
 * material meant opening a panel about a DIFFERENT material first and
 * finding the button at the bottom.
 */
function NewChip({ testId, onClick, title }) {
  return (
    <button
      type="button"
      data-testid={testId}
      className={clsx(styles.brush, styles.brushNew)}
      onClick={onClick}
      title={title}
    >
      + New
    </button>
  );
}

/** A material's or a surface's colour, beside its name. */
function Swatch({ colour }) {
  return <span className={styles.swatch} style={{ background: colour }} aria-hidden="true" />;
}

const TRANSPARENCY = ['opaque', 'translucent', 'transparent'];

/**
 * The materials as chips, and a new one. `prefix` tells the bar's chips
 * (`material-`) from the editor's (`terrain-material-`); only the bar's
 * carry the hardness tooltip.
 */
export function MaterialChips({ build, prefix, withTitles }) {
  return (
    <div className={styles.brushRow} role="radiogroup" aria-label="Material">
      {Object.values(build.materials).map((m) => (
        <RadioChip
          key={m.key}
          checked={build.material === m.key}
          testId={`${prefix}${m.key}`}
          onClick={() => build.setMaterial(m.key)}
          title={withTitles ? `Hardness ${m.hardness} · ${TRANSPARENCY[m.transparency]}` : undefined}
        >
          <Swatch colour={m.colour} />
          {m.name}
        </RadioChip>
      ))}
      <NewChip
        testId={`${prefix}new`}
        onClick={build.addMaterial}
        title={withTitles ? 'A new material, ready to edit' : undefined}
      />
    </div>
  );
}

/** What a surface does, for its chip's tooltip. */
const grantsOf = (surface) => (surface.grants?.length ? surface.grants.join(' · ') : undefined);

/**
 * The surfaces as chips, and a new one. The bar's (`zone-`) name what
 * each surface does and mark the ones that do something with ✦; the
 * editor's (`terrain-surface-`) are plain.
 */
export function SurfaceChips({ build, prefix, newTestId, withTitles }) {
  return (
    <div className={styles.brushRow} role="radiogroup" aria-label="Ground type">
      {Object.values(build.surfaces).map((surface) => (
        <RadioChip
          key={surface.key}
          checked={build.zonePreset === surface.key}
          testId={`${prefix}${surface.key}`}
          onClick={() => build.setZonePreset(surface.key)}
          title={withTitles ? grantsOf(surface) : undefined}
        >
          <Swatch colour={surface.colour} />
          {surface.label}
          {withTitles && surface.grants?.length ? ' ✦' : ''}
        </RadioChip>
      ))}
      <NewChip
        testId={newTestId}
        onClick={build.addSurface}
        title={withTitles ? 'A new surface, ready to edit' : undefined}
      />
    </div>
  );
}
